/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import appConfig from '../../../ui-config.json';

export type TicketResult =
  | { kind: 'ok'; streamUrl: string }
  | { kind: 'reauth' }
  | { kind: 'no-access' }
  | { kind: 'retry-later' };

export const TICKET_PATH = `/${appConfig.appName}-ui-request-handler/stream/session`;
// Through the request handler's invoke proxy, which adds the user's token.
export const STREAM_CLOSE_PATH = `/${appConfig.appName}-ui-request-handler/invoke/api/stream-connections/close`;

export async function requestStreamTicket(forceRefresh: boolean, fetchImpl: typeof fetch = fetch): Promise<TicketResult> {
  let response: Response;
  try {
    response = await fetchImpl(TICKET_PATH, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ forceRefresh }),
    });
  } catch {
    return { kind: 'retry-later' };
  }
  if (response.status === 201) {
    const body = (await response.json().catch(() => null)) as { streamUrl?: unknown } | null;
    return typeof body?.streamUrl === 'string' ? { kind: 'ok', streamUrl: body.streamUrl } : { kind: 'retry-later' };
  }
  if (response.status === 401) {
    return { kind: 'reauth' };
  }
  if (response.status === 403) {
    return { kind: 'no-access' };
  }
  return { kind: 'retry-later' };
}

/**
 * Asks the server to end a stream now. Fire and forget: when it doesn't arrive, the server still ends
 * the stream at its next heartbeat. {@code keepalive} lets it finish while the page unloads.
 */
export function closeStreamConnection(streamId: string, fetchImpl: typeof fetch = fetch): void {
  fetchImpl(STREAM_CLOSE_PATH, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ streamId }),
  }).catch(() => undefined);
}

function streamPath(streamUrl: string, endpoint: string): string {
  const url = new URL(streamUrl, 'http://same-origin.invalid');
  return `${url.pathname.replace(/\/+$/, '')}/${endpoint}`;
}

export function readingStreamUrl(streamUrl: string, serials: string[]): string {
  return `${streamPath(streamUrl, 'meter-readings')}?${new URLSearchParams({ serials: serials.join(',') }).toString()}`;
}

export function instantKpiStreamUrl(streamUrl: string, kpis: string[]): string {
  return `${streamPath(streamUrl, 'instant-kpis')}?${new URLSearchParams({ kpis: kpis.join(',') }).toString()}`;
}

export function windowMetricsStreamUrl(streamUrl: string, keys: string[]): string {
  return `${streamPath(streamUrl, 'window-metrics')}?${new URLSearchParams({ keys: keys.join(',') }).toString()}`;
}

export { checkSession } from '../api/auth';
